import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from './Button.js';
import { IconButton } from './Button.js';

interface BaseProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  caption?: ReactNode;
  children: ReactNode;
  /** Footer buttons. Put the single primary last. */
  footer?: ReactNode;
  /** Slot on the left of the footer (e.g. a destructive text button). */
  footerLeft?: ReactNode;
  wide?: boolean;
}

function useEscape(open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onClose]);
}

/** Centred modal for short forms and confirmations. */
export function Modal({ open, onClose, title, caption, children, footer, footerLeft, wide }: BaseProps) {
  const { t } = useTranslation();
  useEscape(open, onClose);
  if (!open) return null;
  return (
    <div className="ui-overlay ui-overlay--center" onMouseDown={onClose}>
      <div
        className={wide ? 'ui-dialog ui-dialog--wide' : 'ui-dialog'}
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="ui-dialog-header">
          <div>
            <h2 className="ui-dialog-title">{title}</h2>
            {caption && <p className="ui-dialog-caption">{caption}</p>}
          </div>
          <IconButton label={t('common.close')} onClick={onClose}>
            <X />
          </IconButton>
        </div>
        <div className="ui-dialog-body">{children}</div>
        {(footer || footerLeft) && (
          <div className="ui-dialog-footer">
            {footerLeft && <div className="ui-dialog-footer-left">{footerLeft}</div>}
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/** Right-side sheet for create/edit flows that should keep the list in view. */
export function Sheet({ open, onClose, title, caption, children, footer, footerLeft, wide }: BaseProps) {
  const { t } = useTranslation();
  useEscape(open, onClose);
  if (!open) return null;
  return (
    <div className="ui-overlay ui-overlay--right" onMouseDown={onClose}>
      <div
        className={wide ? 'ui-sheet ui-sheet--wide' : 'ui-sheet'}
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="ui-sheet-header">
          <div>
            <h2 className="ui-dialog-title">{title}</h2>
            {caption && <p className="ui-dialog-caption">{caption}</p>}
          </div>
          <IconButton label={t('common.close')} onClick={onClose}>
            <X />
          </IconButton>
        </div>
        <div className="ui-sheet-body">{children}</div>
        {(footer || footerLeft) && (
          <div className="ui-sheet-footer">
            {footerLeft && <div className="ui-dialog-footer-left">{footerLeft}</div>}
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

interface ConfirmProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: ReactNode;
  message?: ReactNode;
  confirmLabel?: string;
  busy?: boolean;
}

/** The only place a filled red button is allowed. */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  busy,
}: ConfirmProps) {
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button variant="destructive-fill" onClick={onConfirm} disabled={busy}>
            {busy ? '…' : (confirmLabel ?? t('common.delete'))}
          </Button>
        </>
      }
    >
      {message && <p className="muted">{message}</p>}
    </Modal>
  );
}
